// src/pages/tenant/Notifications.tsx — the portal's notifications (the phone app's notifications.tsx), opened by the
// bell. GET /notifications (the person's last 50, meta.unread) and POST /notifications/read ({ ids } or all): new ones
// first marked with a gold dot, "Mark all read", and each opens where it points (portalRouteFor) — reading it marks it.
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck, MessagesSquare, Wrench, ReceiptText, KeyRound, FileText, Home, ShieldCheck, type LucideIcon } from 'lucide-react';
import { SkeletonRows } from '../../components/ui';
import { useResidentPortal } from '../../lib/resident';
import { NOTIFICATIONS_KEY, notificationsApi, portalRouteFor, relativeTime, type PortalNotification } from '../../lib/portal';
import { TPageHead, TCard, TEmpty, TBanner } from '../../components/tenant/kit';

const ICON: Record<string, LucideIcon> = { MESSAGE: MessagesSquare, REPAIR: Wrench, RENT: ReceiptText, BOOKING: KeyRound, TENANCY: Home, DOCUMENT: FileText, SCREENING: ShieldCheck };
const iconFor = (n: PortalNotification) => ICON[n.category ?? ''] ?? (n.resource === 'conversations' ? MessagesSquare : n.resource === 'work_orders' ? Wrench : n.resource === 'reservations' ? KeyRound : Bell);

export default function NotificationsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const kind = useResidentPortal() ? 'resident' : 'applicant';
  const q = useQuery({ queryKey: NOTIFICATIONS_KEY, queryFn: notificationsApi.list, refetchInterval: 60_000 });
  const read = useMutation({
    mutationFn: (ids?: string[]) => notificationsApi.read(ids),
    // Show it read at once; the list comes back from the server after.
    onMutate: (ids) => qc.setQueryData<Awaited<ReturnType<typeof notificationsApi.list>>>(NOTIFICATIONS_KEY, (d) => d && {
      items: d.items.map((n) => (!ids || ids.includes(n.id) ? { ...n, readAt: n.readAt ?? new Date().toISOString() } : n)),
      unread: ids ? d.items.filter((n) => !n.readAt && !ids.includes(n.id)).length : 0,
    }),
    onSettled: () => qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
  const items = q.data?.items ?? [];
  const unread = q.data?.unread ?? 0;
  const fresh = items.filter((n) => !n.readAt);
  const earlier = items.filter((n) => n.readAt);

  const openOne = (n: PortalNotification) => {
    if (!n.readAt) read.mutate([n.id]);
    const to = portalRouteFor(n, kind);
    if (to) navigate(to);
  };

  return (
    <div className="space-y-4 sm:space-y-5">
      <TPageHead overline="Your account" title="Notifications" sub={q.data ? (unread ? `${unread} new` : 'You are all caught up.') : undefined}
        action={unread > 0 ? <button type="button" onClick={() => read.mutate(undefined)} disabled={read.isPending} className="t-btn t-btn-soft t-btn-sm"><CheckCheck className="h-4 w-4" />Mark all read</button> : undefined} />
      {q.isError && <TBanner tone="danger">{(q.error as Error).message}</TBanner>}
      {q.isLoading ? <TCard><SkeletonRows rows={4} cols={1} /></TCard>
        : !items.length ? <TEmpty icon={Bell} title="Nothing new" body="Replies from the team, repair updates and news about your home will show here." />
          : (<>
            {fresh.length > 0 && <Group title="New" items={fresh} onOpen={openOne} onRead={(id) => read.mutate([id])} kind={kind} i={1} />}
            {earlier.length > 0 && <Group title="Earlier" items={earlier} onOpen={openOne} kind={kind} i={2} />}
          </>)}
    </div>
  );
}

function Group({ title, items, onOpen, onRead, kind, i }: { title: string; items: PortalNotification[]; onOpen: (n: PortalNotification) => void; onRead?: (id: string) => void; kind: 'resident' | 'applicant'; i: number }) {
  return (
    <TCard title={title} flush i={i}>
      <ul className="divide-y divide-ink-100">
        {items.map((n) => {
          const Icon = iconFor(n);
          const goes = !!portalRouteFor(n, kind);
          const isNew = !n.readAt;
          return (
            <li key={n.id} className={`relative flex items-stretch ${isNew ? 'bg-bronze-50/50' : ''}`}>
              <button type="button" onClick={() => onOpen(n)} className={`flex min-h-[72px] flex-1 items-start gap-3.5 px-5 py-3.5 text-left transition ${goes || isNew ? 'hover:bg-ink-50/80' : 'cursor-default'}`}>
                <span className={`mt-0.5 grid h-11 w-11 shrink-0 place-items-center rounded-full ${isNew ? 'bg-white text-bronze-600 shadow-[var(--shadow-card)]' : 'bg-ink-100 text-ink-500'}`}><Icon className="h-[19px] w-[19px]" /></span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-start gap-2">
                    <span className={`min-w-0 flex-1 text-[15px] leading-snug ${isNew ? 'font-semibold text-ink-900' : 'font-medium text-ink-700'}`}>{n.title}</span>
                    {isNew && <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-bronze-500" aria-label="Unread" />}
                  </span>
                  {n.body && <span className="mt-0.5 line-clamp-2 block text-[13.5px] leading-snug text-ink-500">{n.body}</span>}
                  <span className="mt-1 block text-[12.5px] text-ink-400">{relativeTime(n.createdAt)}</span>
                </span>
              </button>
              {onRead && isNew && (
                <button type="button" onClick={() => onRead(n.id)} aria-label={`Mark "${n.title}" read`} title="Mark read"
                  className="my-auto mr-3 grid h-11 w-11 shrink-0 place-items-center rounded-full text-ink-500 transition hover:bg-white hover:text-bronze-700">
                  <CheckCheck className="h-[18px] w-[18px]" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </TCard>
  );
}
